import { useState, useEffect, useContext, useReducer } from "react";
import { useNavigate } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import Select from 'react-select';
import "../../css/ListUsers.css";
import Header from "../Navigation/Header";
import Footer from "../Navigation/Footer";
import DepartmentReadOnlyRow from "../../components/DepartmentReadOnlyRow";
import DepartmentModal from "../../components/DepartmentModal";
import DepartmentProcesssTable from "../../components/DepartmentProcesssTable";
import FetchUtil from "../../utils/FetchUtil";
import UserContext from "../../contexts/UserContext";
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ddStyles } from "../../utils/CommonStyles.js";
import { scrollToTop } from "../../utils/helper.js";

const initialState = {
	queryPageIndex: 0,
	queryPageSize: 25,
	totalCount: 0,
	queryPageFilter: 0,
};

const PAGE_CHANGED = 'PAGE_CHANGED';
const TOTAL_COUNT_CHANGED = 'TOTAL_COUNT_CHANGED';
const PAGE_FILTER_CHANGED = 'PAGE_FILTER_CHANGED';

const reducer = (state, { type, payload, t }) => {
	switch (type) {
		case PAGE_CHANGED:
			return {
				...state,
				queryPageIndex: payload,
			};
		case TOTAL_COUNT_CHANGED:
			return {
				...state,
				totalCount: payload,
			};
		case PAGE_FILTER_CHANGED:
			return {
				...state,
				queryPageFilter: payload || 0,
				queryPageIndex: 0,
			};
		default:
			throw new Error(t("error.unhandledException") + " : ${type}");
	}
};

let prevCount = 0;
function setPrevCount(newCount) {
	prevCount = newCount;
}


function ListDepartments() {
	const navigate = useNavigate();
	const [show, setShow] = useState(false);
	const [add, setAdd] = useState(false);
	const [errorValue, setErrorValue] = useState("");
	const [data, setData] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [companyOptions, setCompanyOptions] = useState([]);
	const [count, setCount] = useState(0);
	const [companyId, setCompanyId] = useState(0);
	const [companyLoaded, setCompanyLoaded] = useState(false);
	const [companySelected, setCompanySelected] = useState({ name: "companyId", label: "", value: 0 });
	const [deptProcess, setDeptProcess] = useState(false);
	const [processTypes, setProcessTypes] = useState([]);
	const [useFilter, setUseFilter] = useState(false);
	const { t } = useTranslation();
	const userCtx = useContext(UserContext);

	const [columns, setColumns] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	let userCompanyId = (userCtx.role !== "SiteAdmin") ? userCtx.userCompanyId : 0;

	useEffect(() => {
		fetchCompanies();
		fetchProcessTypes();
		setColumns([
			{ accessor: 'departmentName', Header: t("page.dept.name") },
			{ accessor: 'departmentType', Header: t("page.dept.type") },
			{ accessor: 'description', Header: t("page.description") },
			{ accessor: 'address', Header: t("page.address") },
			{ accessor: 'processTypeNames', Header: t("page.process") },
			{ accessor: 'status', Header: t("page.status") },
			{ accessor: 'action', Header: t("page.action"), maxWidth: "10vw" },
		]);
	}, []);

	const handleModalClose = () => {
		setShow(false);
		setAdd(true);
	};
	const initialValues = {
		companyId: 0,
		departmentKey: 0,
		departmentTypeKey: 0,
		companyName: "",
		departmentName: "",
		description: "",
		address1: "",
		address2: "",
		phone: "",
		zipCode: "",
		zipExt: "",
		city: "",
		stateKey: 0,
		stateCode: "",
		countryCode: "US",
		departmentTimeZone: "America/Los_Angeles",
		geocode: "",
		latitude: "",
		longitude: "",
		processTypes: []
	};
	const [editFormData, setEditFormData] = useState(initialValues);

	function fetchCompanies() {
		setErrorValue("");
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ source: "web", getAll: false,
			userCompanyId : userCompanyId }),
		};
		try {
			FetchUtil.fetchAPI("FetchCompanies", "/Service/CompanyService/FetchCompanies", requestOptions, fetchCompaniesSuccess, fetchCompaniesFail);
		} catch (error) {
			throw new Error(error.message);
		}
	}

	function fetchCompaniesFail(redirect) {
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.companyListNotRetrieved"));
	}
	function fetchCompaniesSuccess(responseData) {
		let fetchCompaniesResponse;
		if (responseData) {
			fetchCompaniesResponse = responseData.fetchCompaniesResponse;
			if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "0") {
				setCompanyValues(fetchCompaniesResponse.companies);
				setCompanies(fetchCompaniesResponse.companies);
			} else if (fetchCompaniesResponse && fetchCompaniesResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchCompaniesResponse) {
				setErrorValue(t("error.error") + " " + fetchCompaniesResponse.errorMessage);
			} else {
				setErrorValue(t("error.system.fetch"));
			}
		}
	}
	function setCompanyValues(companyObj) {
		let companyValue = {};
		let companyOptionList = [];
		companyOptionList.push({ label: "", value: 0 });
		if (companyObj && companyObj.length > 0) {
			companyObj.map((company, index) => {
				companyValue.label = company.companyName + ', ' + (company.city ? company.city + ', ' : '') + company.stateCode + ', ' + company.countryCode;
				companyValue.value = company.companyId;
				companyOptionList.push(companyValue);
				companyValue = {};
			});
		}
		setCompanyOptions(companyOptionList);
		setCompanyLoaded(true);
	}

	const companyHandler = (selectedOption, action) => {
		let selectedCompanyId = selectedOption ? selectedOption.value : 0;
		let selectedCompanyName = selectedOption ? selectedOption.label : "";
		setUseFilter(false);
		if (companyId === selectedCompanyId) {
			return
		}

		setCompanyId(selectedCompanyId);
		setCompanySelected({ ...companySelected, label: selectedCompanyName, value: selectedCompanyId });
		if (selectedCompanyId && selectedCompanyId > 0) {
			setUseFilter(true);
			setAdd(true);
		} else {
			setData([]);
			setAdd(false);
		}
	}

	function fetchProcessTypes() {
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ source: "web", getAll: false }),
		};
		try {
			FetchUtil.fetchAPI("MGetProcessTypes", "/Service/StaticHelper/GetProcessTypes", requestOptions, getProcessTypesSuccess, getProcessTypesFail);
		} catch (error) {
			setErrorValue(error.message);
		}
	}

	function getProcessTypesFail(redirect) {
		setErrorValue(t("error.getProcessNamesFailed"));
	}
	function getProcessTypesSuccess(responseData) {
		let getProcessTypesResponse;
		if (responseData) {
			getProcessTypesResponse = responseData.getProcessTypesResponse;
			if (getProcessTypesResponse && getProcessTypesResponse.errorCode === "0") {
				setProcessTypes([...processTypes, ...getProcessTypesResponse.processTypes]);
			} else if (getProcessTypesResponse) {
				setErrorValue(t("error.error") + " " + getProcessTypesResponse.errorMessage);
			}
		}
	}
	const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter }, dispatch] =
		useReducer(reducer, initialState);

	const { isError, error, isSuccess, refetch } = useQuery(
		['department-list', queryPageIndex, queryPageSize, queryPageFilter],
		() => retrieveDepartment(queryPageIndex, queryPageSize, queryPageFilter),
		{
			keepPreviousData: false,
			staleTime: 1000,
			enabled: useFilter,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		}
	);


	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		// Get the state from the instance
		state: { pageIndex, pageSize },
	} = useTable(
		{
			columns,
			data: isSuccess ? data : [],
			initialState: {
				pageIndex: queryPageIndex,
				pageSize: queryPageSize,
			},
			manualPagination: true,
			pageCount: isSuccess ? Math.ceil(count / queryPageSize) : null,
		},
		usePagination
	);

	useEffect(() => {
		dispatch({ type: PAGE_CHANGED, payload: pageIndex, t: t });
	}, [pageIndex]);

	useEffect(() => {
		if (count !== prevCount) {
			dispatch({
				type: TOTAL_COUNT_CHANGED,
				payload: count,
				t: t
			});

			setPrevCount(count);
		}
	}, [count]);

	useEffect(() => {
		if (useFilter) {
			dispatch({ type: PAGE_FILTER_CHANGED, payload: companyId, t: t });
			gotoPage(0);
		}

	}, [companyId, useFilter, gotoPage]);

	function retrieveDepartment(page, dispSize, selectedCompanyInfo) {
		setErrorValue("");
		if (!selectedCompanyInfo || selectedCompanyInfo === 0) {
			return;
		}

		setIsLoading(true);
		const offset = page * dispSize;
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true,
				offset: offset, limit: dispSize, companyId: selectedCompanyInfo
			}),
		};
		try {
			FetchUtil.fetchAPI("GetDepartments", "/Service/CompanyService/GetDepartments", requestOptions, retrieveDepartmentSuccess, retrieveDepartmentFail);
		} catch (error) {
			setIsLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveDepartmentFail(redirect) {
		setIsLoading(false);
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.deptNotRetrieved"));
	}

	function retrieveDepartmentSuccess(responseData) {
		let fetchDepartmentsResponse;
		if (responseData) {
			fetchDepartmentsResponse = responseData.fetchDepartmentsResponse;
			if (fetchDepartmentsResponse && fetchDepartmentsResponse.errorCode === "0") {
				setDeptProcess(false);
				setData(fetchDepartmentsResponse.departments.slice());
				if (fetchDepartmentsResponse.offset === 0) {
					setCount(fetchDepartmentsResponse.totalCount);
				}
			} else if (fetchDepartmentsResponse && fetchDepartmentsResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchDepartmentsResponse) {
				setErrorValue(t("error.error") + " " + fetchDepartmentsResponse.errorMessage);
			} else {
				setErrorValue(t("error.system.fetch"));
			}
		}
		setIsLoading(false);
	}

	const handleAddClick = (event) => {
		event.preventDefault();
		setErrorValue("");
		setShow(true);
		setAdd(true);
		setDeptProcess(false);
		setEditFormData(initialValues);
	}
	const handleEditClick = (event, department) => {
		event.preventDefault();
		event.stopPropagation();
		setErrorValue("");
		let formData = JSON.parse(JSON.stringify(department));
		setEditFormData({ ...editFormData, ...formData });
		setShow(true);
		setAdd(false);
		setDeptProcess(false);
	};

	const handleDeleteClick = (event, departmentKey, statusCode) => {
		event.preventDefault();
		setErrorValue("");
		setAdd(true);
		setDeptProcess(false);
		setShow(false);
		deleteDepartment(companySelected.value, departmentKey, statusCode);
	};

	const handleDeptProcessClick = (event, department) => {
		event.preventDefault();
		event.stopPropagation();
		setErrorValue("");
		let formData = JSON.parse(JSON.stringify(department));
		setEditFormData({ ...editFormData, ...formData });
		setShow(false);
		setAdd(false);
		scrollToTop();
		setDeptProcess(true);
	};

	const deleteDepartment = (selectedCompanyInfo, departmentKey, statusCode) => {
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", companyId: selectedCompanyInfo, departmentKey: departmentKey,
				statusCode: statusCode, updatedById: userCtx.loggedInUserId
			}),
		};
		try {
			FetchUtil.fetchAPI("DeleteDepartment", "/Service/CompanyService/DeleteDepartment", requestOptions, deleteDepartmentSuccess, deleteDepartmentFail);
		} catch (error) {
			setErrorValue(error.message);
		}
	}

	function deleteDepartmentFail(redirect) {
		if (redirect) {
			navigate("/");
		}
		setErrorValue(t("error.deptDeleteFailed"));
	}
	function deleteDepartmentSuccess(responseData) {
		let deleteDepartmentResponse;
		if (responseData) {
			deleteDepartmentResponse = responseData.deleteDepartmentResponse;
			if (deleteDepartmentResponse && deleteDepartmentResponse.errorCode === "0") {
				// only retrieve the modified user
				refetch();
			} else if (deleteDepartmentResponse) {
				refetch();
				setErrorValue(t("error.error") + " " + deleteDepartmentResponse.errorMessage);

			} else {
				setErrorValue(t("error.system.update"));
			}
		}
	}


	if (isError) {
		setErrorValue(t("error.error") + " " + error.message);
	}

	return (
		<div className="container-fluid">
			<Header />
			<div className={(show || deptProcess) ? "container-fluid container_list viewh-85 pe-none" : "container-fluid container_list viewh-85"}>
				{(userCtx.role === "SiteAdmin" || userCtx.role === "ClientAdmin") ?
					<><div className="row">
						<div className="col">
							<div
								id="inerrorMessage"
								className={
									(errorValue) ? "errorblock" : "errorblock hide"
								}
							>
								{errorValue ? errorValue : ""}
							</div>
						</div>
					</div>

						<div className="row">
							<div className="col">
								<div id="placeholder_1" className="placeholder-pg"></div>
							</div>
						</div>
						<div className="row">
							<div className="col">
								<div className="form-group">
									<label className="form-label">
										<span className="form_required">*</span>
										{t("page.company")}
									</label>
									<Select
										name="companyId"
										options={companyOptions}
										onChange={companyHandler}
										placeholder={""}
										value={companySelected}
										isSearchable={companyLoaded}
										isClearable={companyLoaded}
										className="myselect"
										inputId="companyId-input"
										styles={ddStyles}
									/>
								</div>
							</div>
							{(isLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
						</div>
						<div className="row">
							<div className="col">
								<div id="placeholder_2" className="placeholder-pg"></div>
							</div>
						</div>
						{add &&
							<div className="row">
								<div className="col">
									<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#departmentModal"
										onClick={(event) => handleAddClick(event)}>{t("page.dept.add")}</button>
								</div>
							</div>
						}
						<div className="row">
							<div className="col">
								{isSuccess ? (
									<>
										<table className="list-table" {...getTableProps()}>
											<thead>
												{headerGroups.map(headerGroup => (
													<tr {...headerGroup.getHeaderGroupProps()}>
														{headerGroup.headers.map(column => (
															<th className="list-th" {...column.getHeaderProps()}>{column.render("Header")}</th>
														))}
													</tr>
												))}
											</thead>
											<tbody {...getTableBodyProps()}>
												{data.length > 0 && (
													data.map((department, index) => (
														(department) &&
														<DepartmentReadOnlyRow
															t={t}
															department={department}
															handleEditClick={handleEditClick}
															handleDeleteClick={handleDeleteClick}
															handleDeptProcessClick={handleDeptProcessClick}
															oddIdx={((index % 2) > 0) ? true : false}
														/>
													))
												)}
											</tbody>

										</table>

										<div className="pagination">
											<button className="nav-button" onClick={() => { gotoPage(0); }} disabled={!canPreviousPage}>
												{'<<'}
											</button>
											<button className="nav-button" onClick={() => { previousPage(); }} disabled={!canPreviousPage}>
												{'<'}
											</button>
											<button className="nav-button" onClick={() => { nextPage(); }} disabled={!canNextPage}>
												{'>'}
											</button>
											<button className="nav-button"
												onClick={() => { gotoPage(pageCount - 1); }}
												disabled={!canNextPage}
											>
												{'>>'}
											</button>
											<span className="nav-page-info">{t("page.page") + ' '}
												<strong>
													{pageOptions.length > 0 ? pageIndex + 1 : 0} {t("page.of")} {pageOptions.length}
												</strong>
											</span>
											<span className="nav-page-goto">{t("page.goToPage")}:{' '}
												<input
													type="number"
													value={pageIndex + 1}
													onChange={(e) => {
														const page = e.target.value ? Number(e.target.value) - 1 : 0;
														gotoPage(page);
													}}
													className="nav-goto-input"
												/>
											</span>
										</div>
									</>
								) : null}
							</div>
						</div>
						{add &&
							<div className="row">
								<div className="col">
									<button className="btn btn-primary btn-block me-4" data-toggle="modal" data-target="#departmentModal"
										onClick={(event) => handleAddClick(event)}>{t("page.dept.add")}</button>
								</div>
							</div>
						}
						<div className="row">
							<div className="col">
								<div
									id="berrorMessage"
									className={
										(errorValue) ? "errorblock" : "errorblock hide"
									}
								>
									{errorValue ? errorValue : ""}
								</div>
							</div>
						</div>
					</>
					: null}
			</div>
			<div className="footer_container">
				<Footer />
			</div>
			{show && <DepartmentModal t={t} companyList={companies} companyId={companyId} department={editFormData} setShow={setShow} refetch={refetch} add={add} modalClosed={handleModalClose} />}
			{deptProcess &&
				<DepartmentProcesssTable setDeptProcess={setDeptProcess} processTypesForDept={editFormData.processTypes}
					processTypes={processTypes} companyId={editFormData.companyId} departmentKey={editFormData.departmentKey} refetch={refetch} />}
		</div>
	);
}

export default ListDepartments;